import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import MaterialSelect from "../../../components/MasterSelect";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, fetchData, postData } from "../../../services";
import {
  setCurrentPage,
  setRowsPerPage,
  setSelectedCategory,
  setTotalPages,
} from "./store/motorKeyValueStore";
import UploadFile from "../../Utils/UploadFile";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import MotorStoreFilterDialog from './components/MotorStoreFilterDialog.js';

const MotorKeyValueList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.motorKeyValueStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorKeyValueStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.motorKeyValueStore?.totalPages
  );
  const selectedCategory =
    useSelector((state) => state?.motorKeyValueStore?.selectedCategory) || null;
  
  const [isRefresh, setIsRefresh] = useState(false);
  const [categorySelected, setCategorySelected] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [keyValueData, setKeyValueData] = useState([]);
  const [motorCategoryOptions, setMotorCategoryOptions] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    parentKeyId: ""
  });
  
  useEffect(() => {
    if (!selectedCategory?.value) return;

    // Prevent re-calling for same category
    if (categoryId === selectedCategory.value) return;

    handleCategoryChange(selectedCategory,false);
  }, [selectedCategory]);

  useEffect(() => {
    fetchMotorCategoryData();
  }, []);

  useEffect(() => {    
    fetchKeyValueList(selectedCategory?.value || "");
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //fetch category dropdown
  const fetchMotorCategoryData = async () => {
    setCircleLoading(true);
    try {
      const response = await fetchData(`/reference/motor/key-value-category`);

      if (response?.success) {
        setMotorCategoryOptions(
          response?.data?.items.map((item) => ({
            value: item.id,
            label: item.categoryName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    }
    finally{
      setCircleLoading(false);
    }
  };

  const fetchKeyValueList = async (categoryId) => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/key-value-store?categoryId=${categoryId}&page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&parentKeyId=${filterData.parentKeyId}`;
                                    
      //filter
      if(filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }
      const response = await fetchData(url);
      setKeyValueData(response?.data?.items);
      dispatch(setTotalPages(response?.data?.totalPages));
    } catch (error) {
      console.error("Error in fetching Key value Store");
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(
        `/reference/master/bulkUpload/motor-key-value-store`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage || 'Network Error', "error");
    } finally {
      setFileUploading(false);
    }
  };

  //on selecting category api function for key value list
  const handleCategoryChange = async (event, userOnChange = true) => {
    setCategoryName(event?.label || "");
    setCategoryId(event?.value);
    setCategorySelected(true);
    setCircleLoading(true);
    fetchKeyValueList(event?.value);
    if(userOnChange){
      dispatch(setSelectedCategory(event));
    }
  };

  //delete api function
  const handleDelete = async (row) => {
    setCircleLoading(true);
    try {
      const response = await deleteData(`/reference/motor/key-value-store-delete`, {ids:[row?.id]});
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar(response?.message, "success");
      } else {
        showSnackbar(response?.message, "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message || "Network Error", "error");
    }
    finally{
      setCircleLoading(false);
    }
  };

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "keyName",
      label: "Key Name",
      renderCell: (row) => row.keyName,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "categoryId",
      label: "Category",
      renderCell: (row) => row.category?.categoryName,
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = keyValueData.map((keyValue, index) => ({
    ...keyValue,
    sno: index + 1,
    createdAt: new Date(keyValue.createdAt).toLocaleDateString("en-GB"),
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/motor-keyValue/edit-keyValue/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/motor-keyValue/view-keyValue/${row?.id}`);
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewKeyValue = () => {
    navigate("/motor-master/motor-keyValue/add-keyValue", {
      state: { categoryId: categoryId, categoryName: categoryName },
    });
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Key Value Store List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                Select a Category from the dropdown to get the key value stores
                of that particular category.
              </MaterialTypography>
            </Stack>
            {categorySelected && (
              <Stack direction="row" spacing={1}>
                <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt/>}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                Filter
                </MaterialButton>
                <MaterialButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  startIcon={<FileUpload />}
                  onClick={() => setOpenUploadFileDialog(true)}
                >
                  Bulk Upload
                </MaterialButton>
                <MaterialButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddNewKeyValue()}
                >
                  Add {categoryName}
                </MaterialButton>
              </Stack>
            )}
          </Stack>
        }
      />

      <Grid container spacing={2} alignItems="center" sx={{ mx: 2 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <MaterialSelect
            name="categoryName"
            value={selectedCategory}
            customStyles={{ optionPadding: 20 }}
            onChange={handleCategoryChange} //selectedValue, event
            options={motorCategoryOptions?.map((value) => {
              return {
                value: value.value,
                label: value.label,
              };
            })}
            success={selectedCategory?.value}            
          />
        </Grid>
      </Grid>
      <Card sx={{ mx: 1, mt: 2 }}>

          <Table
            columns={columns}
            data={rows}
            actions={actions}
            pagination={{
              totalPages,
              currentPage,
              onPageChange: (page) => handlePaginationChange(page),
              rowsPerPage,
              onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
              rowsPerPageOptions: [5, 10, 15, 20],
            }}
            getRowKey={(row) => row.id}
            className="custom-table"
            noDataLabel={"No Key Value data found"}
            handleSearch={handleSearch}
          />

      </Card>
      <MotorStoreFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);
          fetchKeyValueList(categoryId); 
      }}
      />
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
    </DashboardLayout>
  );
};

export default MotorKeyValueList;
